import { useCallback, useEffect, useMemo, useRef, useState } from "react";

type UseAutofillClampNumberArgs = {
    max: number | undefined | null;
    enabled: boolean;
    gateAutofill: boolean;
    onValueChange?: (v: number | undefined) => void;
    /** When set (finite, ≥ 0), autofill uses min(this, max) instead of max. */
    autofillTarget?: number | undefined | null;
};

export function useAutofillClampNumber({
    max,
    enabled,
    gateAutofill,
    onValueChange,
    autofillTarget
}: UseAutofillClampNumberArgs) {
    const [value, _setValue] = useState<number | undefined>(undefined);
    const [isUserEdited, setIsUserEdited] = useState(false);

    const hasSeenMaxRef = useRef(false);
    useEffect(() => {
        if (max != null) hasSeenMaxRef.current = true;
    }, [max]);

    const setValueRaw = useCallback(
        (v: number | undefined) => {
            _setValue(v);
            onValueChange?.(v);
        },
        [onValueChange]
    );

    const setValue = useCallback(
        (v: number | undefined) => {
            setIsUserEdited(true);
            setValueRaw(v);
        },
        [setValueRaw]
    );

    const setMax = useCallback(() => {
        setIsUserEdited(true);
        setValueRaw((max ?? 0) as number);
    }, [max, setValueRaw]);

    useEffect(() => {
        if (!enabled) return;
        if (!gateAutofill) return;
        if (isUserEdited) return;
        if (!hasSeenMaxRef.current) return;
        if (max == null) return;
        const useTarget =
            autofillTarget != null && Number.isFinite(autofillTarget) && autofillTarget >= 0
                ? Math.min(autofillTarget, max)
                : max;
        setValueRaw(useTarget);
    }, [enabled, gateAutofill, isUserEdited, max, autofillTarget, setValueRaw]);

    useEffect(() => {
        if (!enabled) return;
        if (max == null) return;
        if (value == null) return;
        if (value > max) setValueRaw(max);
    }, [enabled, max, value, setValueRaw]);

    const reset = useCallback(() => {
        setValueRaw(undefined);
        setIsUserEdited(false);
    }, [setValueRaw]);

    return useMemo(
        () => ({
            value,
            setValue,
            setValueRaw,
            setMax,
            isUserEdited,
            reset
        }),
        [value, setValue, setValueRaw, setMax, isUserEdited, reset]
    );
}
